"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ClipboardEvent,
  type CSSProperties,
  type DragEvent,
} from "react";
import type { Sperrgrad } from "@/dev-testbed/testlauf/typen.ts";
import { useTestScope } from "./use-test-scope.ts";

export type VorgabeInputProps = {
  value?: string;
  readOnly?: boolean;
  "data-test-feld": string;
  "aria-describedby"?: string;
  "aria-readonly"?: boolean;
  autoComplete?: string;
  placeholder?: string;
  style?: CSSProperties;
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
  onDrop?: (e: DragEvent<HTMLInputElement>) => void;
  onCut?: (e: ClipboardEvent<HTMLInputElement>) => void;
};

export type UseVorgabeErgebnis = {
  aktiv: boolean;
  wert: string | null;
  sperre: Sperrgrad | null;
  hinweis: string | null;
  inputProps: VorgabeInputProps;
  /** DOM-ID des Badges für aria-describedby / VorgabeHinweis */
  hinweisId: string;
  /**
   * onChange über den vorhandenen Setter. Blockiert harte Sperren;
   * protokolliert weiche Abweichungen einmalig.
   */
  onChangeWert(neu: string, setValue: (v: string) => void): void;
};

type Binding = {
  value: string;
  setValue: (v: string) => void;
};

const HART_STIL: CSSProperties = {
  backgroundColor: "#18181c",
  color: "#a1a1aa",
  cursor: "default",
};

function blockiereEingabe(e: { preventDefault(): void }): void {
  e.preventDefault();
}

/**
 * Bindet ein Formularfeld an die aufgelöste Vorgabe des aktiven Testlaufs.
 * Ohne Scope: `aktiv: false` und `inputProps` nur mit `data-test-feld`.
 *
 * Optional `binding`: synchronisiert Anfangswert (hart/weich) und setzt bei
 * harter Sperre Autofill-/Paste-Abweichungen zurück.
 */
export function useVorgabe(feld: string, binding?: Binding): UseVorgabeErgebnis {
  const scope = useTestScope();
  const vorgabe = scope.vorgabeFuer(feld);
  const reactId = useId();
  const hinweisId = `vorgabe-hinweis-${feld.replace(/\./g, "-")}-${reactId.replace(/:/g, "")}`;
  const softGemeldet = useRef(false);
  const hartZurueckGemeldet = useRef(false);
  const initialSynced = useRef(false);

  const aktiv = scope.aktiv && vorgabe !== null;
  const wert = vorgabe?.wert ?? null;
  const sperre = vorgabe?.sperre ?? null;
  const hinweis = vorgabe?.hinweis ?? null;

  const boundValue = binding?.value;
  const setBoundValue = binding?.setValue;

  useEffect(() => {
    softGemeldet.current = false;
    hartZurueckGemeldet.current = false;
    initialSynced.current = false;
  }, [wert, sperre, feld]);

  // Anfangswert einmal setzen, wenn Vorgabe aktiv wird (hart/weich)
  useEffect(() => {
    if (!setBoundValue || !aktiv || wert === null) return;
    if (sperre !== "hart" && sperre !== "weich") return;
    if (initialSynced.current) return;
    initialSynced.current = true;
    if (boundValue !== wert) setBoundValue(wert);
  }, [aktiv, wert, sperre, setBoundValue, boundValue]);

  // Harte Sperre: Abweichung zurücksetzen und einmal melden
  useEffect(() => {
    if (!setBoundValue || !aktiv || sperre !== "hart" || wert === null) return;
    if (boundValue === undefined) return;
    if (boundValue === wert) {
      hartZurueckGemeldet.current = false;
      return;
    }
    setBoundValue(wert);
    if (!hartZurueckGemeldet.current) {
      hartZurueckGemeldet.current = true;
      scope.meldeEreignis("aktion", `vorgabe-zurueckgesetzt:${feld}`);
    }
  }, [boundValue, setBoundValue, aktiv, sperre, wert, scope, feld]);

  const onChangeWert = useCallback(
    (neu: string, setValue: (v: string) => void) => {
      if (!aktiv || sperre === null) {
        setValue(neu);
        return;
      }
      if (sperre === "hart") {
        if (wert !== null) setValue(wert);
        return;
      }
      setValue(neu);
      if (sperre === "weich" && wert !== null && neu !== wert) {
        if (!softGemeldet.current) {
          softGemeldet.current = true;
          scope.meldeEreignis("aktion", `vorgabe-geaendert:${feld}`);
        }
      } else if (neu === wert) {
        softGemeldet.current = false;
      }
    },
    [aktiv, sperre, wert, scope, feld],
  );

  const inputProps: VorgabeInputProps = {
    "data-test-feld": feld,
  };

  if (!aktiv || !vorgabe) {
    return {
      aktiv: false,
      wert: null,
      sperre: null,
      hinweis: null,
      inputProps,
      hinweisId,
      onChangeWert,
    };
  }

  inputProps["aria-describedby"] = hinweisId;

  if (sperre === "hart") {
    inputProps.value = wert ?? undefined;
    inputProps.readOnly = true;
    inputProps["aria-readonly"] = true;
    inputProps.autoComplete = "off";
    inputProps.style = HART_STIL;
    inputProps.onPaste = blockiereEingabe;
    inputProps.onDrop = blockiereEingabe;
    inputProps.onCut = blockiereEingabe;
  } else if (sperre === "vorschlag") {
    inputProps.placeholder = wert ?? undefined;
  }

  return {
    aktiv: true,
    wert,
    sperre,
    hinweis,
    inputProps,
    hinweisId,
    onChangeWert,
  };
}
